import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Investors from "@/pages/Investors";
import TenantAdvice from "@/pages/TenantAdvice";
import HowWeWork from "@/pages/HowWeWork";
import Contact from "@/pages/Contact";
import HmoServices from "@/pages/HmoServices";
import StampDuty from "@/pages/StampDuty";
import Faq from "@/pages/Faq";
import Insights from "@/pages/Insights";
import InsightsPost from "@/pages/InsightsPost";
import TermsAndConditions from "@/pages/TermsAndConditions";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ComplaintsProcedure from "@/pages/ComplaintsProcedure";
import AdminApp from "@/admin/AdminApp";
import PublicForm from "@/pages/PublicForm";
import PublicAgreement from "@/pages/PublicAgreement";
import AgencyApp from "@/agency/AgencyApp";
import { ContentProvider } from "@/context/ContentContext";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15 });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);
};

const AppInner = () => {
  useLenis();
  const location = useLocation();
  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          <Routes location={location}>
            <Route path="/admin" element={<AdminApp />} />
            <Route path="/apply/:token" element={<PublicForm />} />
            <Route path="/agreement/:token" element={<PublicAgreement />} />
            <Route path="/agency/*" element={<AgencyApp />} />
            <Route path="/portal/*" element={<AgencyApp />} />
            <Route path="/broker/*" element={<AgencyApp />} />
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/investors" element={<Investors />} />
            <Route path="/hmo-services" element={<HmoServices />} />
            <Route path="/tenant-leasehold-advice" element={<TenantAdvice />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/stamp-duty-calculator" element={<StampDuty />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightsPost />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/complaints-procedure" element={<ComplaintsProcedure />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ContentProvider>
          <AppInner />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#0F3226",
                color: "#F5F0E6",
                border: "1px solid rgba(198,164,94,0.4)",
                borderRadius: "2px",
                fontFamily: "Manrope, sans-serif",
              },
            }}
          />
        </ContentProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
